import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { api } from '../scripts/api';

const STATUSES = [
  { key: 'ok', label: 'OK' },
  { key: 'not_ok', label: 'Not OK' },
  { key: 'repaired', label: 'Repaired' },
  { key: 'na', label: 'N/A' },
];

const CANDIDATE_TABLES = ['all_inventory', 'stock_inventory', 'used_inventory', 'sold_inventory'];

export default function PdiPage() {
  const { id } = useParams(); // inventory id
  const location = useLocation();
  const queryTable = new URLSearchParams(location.search).get('table');
  const [inventory, setInventory] = useState(null);
  const [table, setTable] = useState(null);
  const [template, setTemplate] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [signDataUrl, setSignDataUrl] = useState('');
  const [signMsg, setSignMsg] = useState('');
  const [sessionStatus, setSessionStatus] = useState('in_progress');
  const [techInitials, setTechInitials] = useState('');
  const [engInitials, setEngInitials] = useState('');

  // Load inventory + start session
  useEffect(() => {
    const load = async () => {
      try {
        setBusy(true);
        // try to find the record across candidate tables
        let found = null;
        let foundTable = null;
        const tablesToTry = queryTable ? [queryTable, ...CANDIDATE_TABLES.filter((t) => t !== queryTable)] : CANDIDATE_TABLES;

        for (const t of tablesToTry) {
          try {
            const invRes = await api.get(`/${t}/${id}`);
            found = invRes.data;
            foundTable = t;
            break;
          } catch (e) {
            // continue to next
          }
        }
        if (!found) {
          setMsg(`Record ${id} not found in any table (${CANDIDATE_TABLES.join(', ')})`);
          return;
        }
        setInventory(found);
        setTable(foundTable);

        const { data: tpl } = await api.get('/pdi/templates/tana_compactor');
        setTemplate(tpl);

        const { data: session } = await api.post('/pdi/sessions', {
          inventory_id: id,
          table: foundTable,
          template: tpl.template,
          meta: { vin: found?.body_vin || found?.chassis_vin || '' },
        });
        setSessionId(session.session_id);
        setMsg('');
      } catch (e) {
        console.error('PDI load failed', e.response?.data || e.message);
        const detail = e.response?.data?.error || e.response?.data?.detail || e.message;
        setMsg(`Failed to load PDI session: ${detail}`);
      } finally {
        setBusy(false);
      }
    };
    load();
  }, [id]);

  // Load responses once session exists
  useEffect(() => {
    if (!sessionId) return;
    const fetchResponses = async () => {
      const { data } = await api.get(`/pdi/sessions/${sessionId}`);
      const map = {};
      (data.responses || []).forEach((r) => {
        map[r.step_id] = { status: r.status, comment: r.comment || '', photo: r.photo_path || '' };
      });
      setResponses(map);
      if (data.session?.table_name) setTable(data.session.table_name);
      if (data.session?.signature_path) setSignDataUrl(data.session.signature_path);
      if (data.session?.status) setSessionStatus(data.session.status);
      if (data.session?.tech_initials) setTechInitials(data.session.tech_initials);
      if (data.session?.engineering_initials) setEngInitials(data.session.engineering_initials);
    };
    fetchResponses();
  }, [sessionId]);

  const handleStatus = async (stepId, status) => {
    if (!sessionId) return;
    setResponses((prev) => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), status } }));
    await api.post(`/pdi/sessions/${sessionId}/step`, { step_id: stepId, status, comment: responses[stepId]?.comment || '' });
  };

  const handleComment = async (stepId, comment) => {
    if (!sessionId) return;
    setResponses((prev) => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), comment } }));
    await api.post(`/pdi/sessions/${sessionId}/step`, { step_id: stepId, status: responses[stepId]?.status || 'not_ok', comment });
  };

  const uploadPhoto = async (stepId, file) => {
    if (!sessionId || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('step_id', stepId);
    try {
      const { data } = await api.post(`/pdi/sessions/${sessionId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResponses((prev) => ({
        ...prev,
        [stepId]: { ...(prev[stepId] || {}), photo: data.url, status: prev[stepId]?.status || 'na' },
      }));
    } catch (e) {
      console.error(e);
      setMsg('Photo upload failed');
    }
  };

  const allDone = useMemo(() => {
    if (!template) return false;
    const total = template.sections.flatMap((s) => s.steps).length;
    const answered = Object.keys(responses).length;
    return answered >= total;
  }, [template, responses]);

  const complete = async () => {
    if (!sessionId) return;
    await api.post(`/pdi/sessions/${sessionId}/complete`, { tech_initials: techInitials });
    setSessionStatus('completed');
    setMsg('PDI completed and status updated');
  };

  const canvasId = 'pdi-signature';

  // init simple canvas draw handlers
  useEffect(() => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    let drawing = false;
    let last = null;
    const pos = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const p = ev.touches ? ev.touches[0] : ev;
      return { x: p.clientX - rect.left, y: p.clientY - rect.top };
    };
    const start = (ev) => { drawing = true; last = pos(ev); };
    const move = (ev) => {
      if (!drawing || !last) return;
      const { x, y } = pos(ev);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(x, y); ctx.stroke();
      last = { x, y };
    };
    const end = () => { drawing = false; last = null; };
    canvas.onmousedown = start;
    canvas.onmousemove = move;
    window.onmouseup = end;
    canvas.ontouchstart = (e) => { e.preventDefault(); start(e); };
    canvas.ontouchmove = (e) => { e.preventDefault(); move(e); };
    canvas.ontouchend = end;
  }, [sessionId]);

  const clearSignature = () => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setSignDataUrl('');
    setSignMsg('');
  };

  const saveSignature = () => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    if (sessionStatus !== 'completed') {
      setSignMsg('Complete the PDI before signing.');
      return;
    }
    if (!engInitials.trim()) {
      setSignMsg('Engineering initials required.');
      return;
    }
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const fd = new FormData();
      fd.append('file', blob, 'signature.png');
      fd.append('initials', engInitials.trim());
      try {
        const { data } = await api.post(`/pdi/sessions/${sessionId}/signature`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSignDataUrl(data.url);
        setSignMsg('Signature saved');
      } catch (e) {
        console.error(e);
        setSignMsg('Signature save failed');
      }
    });
  };

  return (
    <div className="container">
      <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="muted">Inventory ID</div>
          <div>{id}</div>
        </div>
        {inventory?.body_vin && (
          <div>
            <div className="muted">VIN</div>
            <div>{inventory.body_vin}</div>
          </div>
        )}
        {inventory?.rowo && (
          <div>
            <div className="muted">RO/WO</div>
            <div>{inventory.rowo}</div>
          </div>
        )}
        {allDone && (
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ maxWidth: 160 }}
              placeholder="Tech initials"
              value={techInitials}
              onChange={(e) => setTechInitials(e.target.value)}
            />
            <button className="btn" onClick={complete} disabled={!techInitials.trim()}>
              Complete PDI
            </button>
          </div>
        )}
      </div>

      {busy && <div className="muted">Loading…</div>}
      {msg && <div className="muted">{msg}</div>}

      {template?.sections.map((section) => (
        <div key={section.title} style={{ marginTop: 20, padding: 12, border: '1px solid #ddd', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{section.title}</div>
          {section.steps.map((step) => {
            const resp = responses[step.id] || {};
            return (
              <div key={step.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{step.id}. {step.item}</div>
                    <div className="muted" style={{ maxWidth: 720 }}>{step.instruction}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        className={resp.status === s.key ? 'btn' : 'btn-outline'}
                        onClick={() => handleStatus(step.id, s.key)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  className="input"
                  style={{ marginTop: 6, width: '100%', minHeight: 60 }}
                  placeholder="Comments / measurements / initials"
                  value={resp.comment || ''}
                  onChange={(e) => handleComment(step.id, e.target.value)}
                />
                <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                  <label className="btn-outline" style={{ margin: 0 }}>
                    Upload photo
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => e.target.files?.[0] && uploadPhoto(step.id, e.target.files[0])} />
                  </label>
                  {resp.photo && <a href={resp.photo} target="_blank" rel="noreferrer" className="btn-outline">View photo</a>}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <div className="card" style={{ marginTop: 20, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700 }}>Engineering Signature</div>
            <div className="muted">
              Allowed only after completion. Capture on tablet; saved with session.
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ maxWidth: 160 }}
              placeholder="Eng initials"
              value={engInitials}
              onChange={(e) => setEngInitials(e.target.value)}
              disabled={sessionStatus !== 'completed'}
            />
            <button className="btn-outline" onClick={clearSignature} disabled={sessionStatus !== 'completed'}>
              Clear
            </button>
            <button className="btn" onClick={saveSignature} disabled={sessionStatus !== 'completed'}>
              Save Signature
            </button>
          </div>
        </div>
        <canvas
          id={canvasId}
          width={360}
          height={160}
          style={{ width: '100%', maxWidth: 480, border: '1px dashed #ccc', borderRadius: 8, marginTop: 10, touchAction: 'none', opacity: sessionStatus === 'completed' ? 1 : 0.4 }}
        />
        {signDataUrl && (
          <div style={{ marginTop: 8 }}>
            <a className="btn-outline" href={signDataUrl.startsWith('http') ? signDataUrl : `/api${signDataUrl}`} target="_blank" rel="noreferrer">
              View saved signature
            </a>
            {signMsg && <span className="muted" style={{ marginLeft: 8 }}>{signMsg}</span>}
          </div>
        )}
        {sessionStatus !== 'completed' && <div className="muted" style={{ marginTop: 6 }}>Complete the PDI first.</div>}
      </div>
    </div>
  );
}
